import type { IControlSettingUI } from '../../type';
import type { RMetricGroupType } from '../types';

import { getMetricSettingUI } from '../utils/metricUI';
import { IRMetricKey } from '../types';

export const REPORT_METRIC_MAPPING: Record<
  IRMetricKey,
  {
    label: string;
    value: IRMetricKey;
  }
> = {
  [IRMetricKey.AOV]: {
    label: 'Average order value',
    value: IRMetricKey.AOV,
  },
  [IRMetricKey.ORDERS]: {
    label: 'Orders',
    value: IRMetricKey.ORDERS,
  },
  [IRMetricKey.REVENUE]: {
    label: 'Revenue',
    value: IRMetricKey.REVENUE,
  },
  [IRMetricKey.RPV]: {
    label: 'Revenue per visitor',
    value: IRMetricKey.RPV,
  },
  [IRMetricKey.AVG_TIME_ON_PAGE]: {
    label: 'Average time on page',
    value: IRMetricKey.AVG_TIME_ON_PAGE,
  },
  [IRMetricKey.BOUNCE_RATE]: {
    label: 'Bounce rate',
    value: IRMetricKey.BOUNCE_RATE,
  },
  [IRMetricKey.PAGE_VIEWS]: {
    label: 'Pageviews',
    value: IRMetricKey.PAGE_VIEWS,
  },
  [IRMetricKey.SESSION]: {
    label: 'Sessions',
    value: IRMetricKey.SESSION,
  },
  [IRMetricKey.CART_ADDITION]: {
    label: 'Sessions with cart additions',
    value: IRMetricKey.CART_ADDITION,
  },
  [IRMetricKey.REACHED_CHECKOUT]: {
    label: 'Sessions that reached checkout',
    value: IRMetricKey.REACHED_CHECKOUT,
  },
  [IRMetricKey.COMPLETE_CHECKOUT]: {
    label: 'Sessions that completed checkout',
    value: IRMetricKey.COMPLETE_CHECKOUT,
  },
  [IRMetricKey.VISITORS]: {
    label: 'Visitors',
    value: IRMetricKey.VISITORS,
  },
  [IRMetricKey.ADDED_TO_CART]: {
    label: 'Added to cart',
    value: IRMetricKey.ADDED_TO_CART,
  },
  [IRMetricKey.ADD_TO_CART_RATE]: {
    label: 'Add to cart rate',
    value: IRMetricKey.ADD_TO_CART_RATE,
  },
  [IRMetricKey.CONVERSION_RATE]: {
    label: 'Conversion rate',
    value: IRMetricKey.CONVERSION_RATE,
  },
  [IRMetricKey.CTR]: {
    label: 'Click-through rate',
    value: IRMetricKey.CTR,
  },

  [IRMetricKey.SESSION_BY_DEVICE_TYPE]: {
    label: 'Session by device type',
    value: IRMetricKey.SESSION_BY_DEVICE_TYPE,
  },
  [IRMetricKey.SESSION_BY_TRAFFIC_SOURCE]: {
    label: 'Session by traffic source',
    value: IRMetricKey.SESSION_BY_TRAFFIC_SOURCE,
  },
  [IRMetricKey.VISITOR_BY_TYPE]: {
    label: 'Visitor by type',
    value: IRMetricKey.VISITOR_BY_TYPE,
  },
};

const SALE_METRIC_OPTIONS = [
  REPORT_METRIC_MAPPING[IRMetricKey.AOV],
  REPORT_METRIC_MAPPING[IRMetricKey.ORDERS],
  REPORT_METRIC_MAPPING[IRMetricKey.REVENUE],
  REPORT_METRIC_MAPPING[IRMetricKey.RPV],
];

const SESSION_METRIC_OPTIONS = [
  REPORT_METRIC_MAPPING[IRMetricKey.AVG_TIME_ON_PAGE],
  REPORT_METRIC_MAPPING[IRMetricKey.BOUNCE_RATE],
  REPORT_METRIC_MAPPING[IRMetricKey.PAGE_VIEWS],
  REPORT_METRIC_MAPPING[IRMetricKey.SESSION],
  REPORT_METRIC_MAPPING[IRMetricKey.CART_ADDITION],
  REPORT_METRIC_MAPPING[IRMetricKey.REACHED_CHECKOUT],
  REPORT_METRIC_MAPPING[IRMetricKey.COMPLETE_CHECKOUT],
  REPORT_METRIC_MAPPING[IRMetricKey.VISITORS],
];

const BEHAVIOR_METRIC_OPTIONS = [
  REPORT_METRIC_MAPPING[IRMetricKey.ADDED_TO_CART],
  REPORT_METRIC_MAPPING[IRMetricKey.ADD_TO_CART_RATE],
  REPORT_METRIC_MAPPING[IRMetricKey.CONVERSION_RATE],
  REPORT_METRIC_MAPPING[IRMetricKey.CTR],
];

export const REPORT_METRIC_GROUP: RMetricGroupType = [
  {
    title: 'Sale',
    options: SALE_METRIC_OPTIONS,
  },
  {
    title: 'Session',
    options: SESSION_METRIC_OPTIONS,
  },
  {
    title: 'Behavior',
    options: BEHAVIOR_METRIC_OPTIONS,
  },
];

export const REPORT_METRIC_UI: Record<IRMetricKey, IControlSettingUI> = {
  [IRMetricKey.AOV]: getMetricSettingUI(
    IRMetricKey.AOV,
    REPORT_METRIC_MAPPING[IRMetricKey.AOV].label,
  ),
  [IRMetricKey.ORDERS]: getMetricSettingUI(
    IRMetricKey.ORDERS,
    REPORT_METRIC_MAPPING[IRMetricKey.ORDERS].label,
  ),
  [IRMetricKey.REVENUE]: getMetricSettingUI(
    IRMetricKey.REVENUE,
    REPORT_METRIC_MAPPING[IRMetricKey.REVENUE].label,
  ),
  [IRMetricKey.RPV]: getMetricSettingUI(
    IRMetricKey.RPV,
    REPORT_METRIC_MAPPING[IRMetricKey.RPV].label,
  ),
  [IRMetricKey.AVG_TIME_ON_PAGE]: getMetricSettingUI(
    IRMetricKey.AVG_TIME_ON_PAGE,
    REPORT_METRIC_MAPPING[IRMetricKey.AVG_TIME_ON_PAGE].label,
  ),
  [IRMetricKey.BOUNCE_RATE]: getMetricSettingUI(
    IRMetricKey.BOUNCE_RATE,
    REPORT_METRIC_MAPPING[IRMetricKey.BOUNCE_RATE].label,
  ),
  [IRMetricKey.PAGE_VIEWS]: getMetricSettingUI(
    IRMetricKey.PAGE_VIEWS,
    REPORT_METRIC_MAPPING[IRMetricKey.PAGE_VIEWS].label,
  ),
  [IRMetricKey.SESSION]: getMetricSettingUI(
    IRMetricKey.SESSION,
    REPORT_METRIC_MAPPING[IRMetricKey.SESSION].label,
  ),
  [IRMetricKey.CART_ADDITION]: getMetricSettingUI(
    IRMetricKey.CART_ADDITION,
    REPORT_METRIC_MAPPING[IRMetricKey.CART_ADDITION].label,
  ),
  [IRMetricKey.REACHED_CHECKOUT]: getMetricSettingUI(
    IRMetricKey.REACHED_CHECKOUT,
    REPORT_METRIC_MAPPING[IRMetricKey.REACHED_CHECKOUT].label,
  ),
  [IRMetricKey.COMPLETE_CHECKOUT]: getMetricSettingUI(
    IRMetricKey.COMPLETE_CHECKOUT,
    REPORT_METRIC_MAPPING[IRMetricKey.COMPLETE_CHECKOUT].label,
  ),
  [IRMetricKey.VISITORS]: getMetricSettingUI(
    IRMetricKey.VISITORS,
    REPORT_METRIC_MAPPING[IRMetricKey.VISITORS].label,
  ),
  [IRMetricKey.ADDED_TO_CART]: getMetricSettingUI(
    IRMetricKey.ADDED_TO_CART,
    REPORT_METRIC_MAPPING[IRMetricKey.ADDED_TO_CART].label,
  ),
  [IRMetricKey.ADD_TO_CART_RATE]: getMetricSettingUI(
    IRMetricKey.ADD_TO_CART_RATE,
    REPORT_METRIC_MAPPING[IRMetricKey.ADD_TO_CART_RATE].label,
  ),
  [IRMetricKey.CONVERSION_RATE]: getMetricSettingUI(
    IRMetricKey.CONVERSION_RATE,
    REPORT_METRIC_MAPPING[IRMetricKey.CONVERSION_RATE].label,
  ),
  [IRMetricKey.CTR]: getMetricSettingUI(
    IRMetricKey.CTR,
    REPORT_METRIC_MAPPING[IRMetricKey.CTR].label,
  ),
  [IRMetricKey.SESSION_BY_DEVICE_TYPE]: getMetricSettingUI(
    IRMetricKey.SESSION_BY_DEVICE_TYPE,
    REPORT_METRIC_MAPPING[IRMetricKey.SESSION_BY_DEVICE_TYPE].label,
  ),
  [IRMetricKey.SESSION_BY_TRAFFIC_SOURCE]: getMetricSettingUI(
    IRMetricKey.SESSION_BY_TRAFFIC_SOURCE,
    REPORT_METRIC_MAPPING[IRMetricKey.SESSION_BY_TRAFFIC_SOURCE].label,
  ),
  [IRMetricKey.VISITOR_BY_TYPE]: getMetricSettingUI(
    IRMetricKey.VISITOR_BY_TYPE,
    REPORT_METRIC_MAPPING[IRMetricKey.VISITOR_BY_TYPE].label,
  ),
};
